import { Container, Button, Label } from '@playcanvas/pcui/react';
import React from 'react';

import { SetProperty, ObserverData } from '../types';
import { Vector } from './components';

const coordinatesExpandedStorageKey = 'model-viewer:selected-node-coordinates-expanded';

const getInitialCoordinatesExpanded = () => {
    if (typeof window === 'undefined') {
        return true;
    }

    try {
        return window.sessionStorage?.getItem(coordinatesExpandedStorageKey) !== 'false';
    } catch {
        return true;
    }
};

// Helper function to copy text to clipboard
const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
        console.log('Copied to clipboard:', text);
    }).catch(err => {
        console.error('Failed to copy:', err);
    });
};

// Row component for label + value with optional copy button
const Row = (props: { label: string, value: string | number | undefined, copyText?: string, showCopy?: boolean }) => {
    if (props.value === undefined || props.value === null) return null;
    
    const textToCopy = props.copyText || String(props.value);
    const showCopy = props.showCopy !== false && props.copyText;
    
    return (
        <div className='selected-node-row'>
            <span className='selected-node-label'>{props.label}</span>
            <span className='selected-node-value'>{String(props.value)}</span>
            {showCopy && (
                <button
                    className='selected-node-copy'
                    onClick={() => copyToClipboard(textToCopy)}
                    title='Copy to clipboard'
                >
                    📋
                </button>
            )}
        </div>
    );
};

// Multi-selection panel view
const MultiSelectionPanel = (props: { 
    nodes: Array<{ name: string, path: string, meshName?: string }>,
    onClear: () => void
}) => {
    const { nodes, onClear } = props;
    
    // Copy all names as newline-separated list
    const copyAllNames = () => {
        const names = nodes.map(n => n.meshName || n.name).join('\n');
        copyToClipboard(names);
    };
    
    return (
        <div className='selected-node-panel multi-select-mode'>
            <div className='multi-select-header'>
                <span className='multi-select-title'>Selected ({nodes.length})</span>
                <button className='selected-node-copy' onClick={onClear} title='Clear selection'>✕</button>
            </div>
            <div className='multi-select-list'>
                {nodes.map((node, index) => (
                    <div key={node.path} className='multi-select-item'>
                        <span className='multi-select-name'>{node.meshName || node.name}</span>
                        <button
                            className='selected-node-copy'
                            onClick={() => copyToClipboard(node.meshName || node.name)}
                            title='Copy name'
                        >
                            📋
                        </button>
                    </div>
                ))}
            </div>
            <button className='multi-select-copy-all' onClick={copyAllNames}>
                Copy All Names
            </button>
        </div>
    );
};

interface SelectedNodeProps {
    sceneData: ObserverData['scene'],
    setProperty?: SetProperty
}

interface SelectedNodeState {
    coordinatesExpanded: boolean
}

class SelectedNode extends React.Component < SelectedNodeProps, SelectedNodeState > {
    state: SelectedNodeState = {
        coordinatesExpanded: getInitialCoordinatesExpanded()
    };

    shouldComponentUpdate(
        nextProps: Readonly<SelectedNodeProps>,
        nextState: Readonly<SelectedNodeState>
    ): boolean {
        const current = this.props.sceneData.selectedNode;
        const next = nextProps.sceneData.selectedNode;
        
        if (nextState.coordinatesExpanded !== this.state.coordinatesExpanded) return true;

        // Check multi-selection changes
        const currentMulti = this.props.sceneData.multiSelectedNodes || [];
        const nextMulti = nextProps.sceneData.multiSelectedNodes || [];
        if (JSON.stringify(currentMulti) !== JSON.stringify(nextMulti)) return true;
        
        if (current.path !== next.path) return true;
        if (current.meshName !== next.meshName) return true;
        if (current.materialName !== next.materialName) return true;
        if (JSON.stringify(current.animations) !== JSON.stringify(next.animations)) return true;
        if (this.state.coordinatesExpanded || nextState.coordinatesExpanded) {
            if (JSON.stringify(current.worldPosition) !== JSON.stringify(next.worldPosition)) {
                return true;
            }
            if (JSON.stringify(current.worldRotation) !== JSON.stringify(next.worldRotation)) {
                return true;
            }
            if (JSON.stringify(current.worldScale) !== JSON.stringify(next.worldScale)) {
                return true;
            }
            if (JSON.stringify(current.position) !== JSON.stringify(next.position)) return true;
            if (JSON.stringify(current.rotation) !== JSON.stringify(next.rotation)) return true;
            if (JSON.stringify(current.scale) !== JSON.stringify(next.scale)) return true;
        }
        if (current.name !== next.name) return true;
        if (nextProps.sceneData.nodes !== this.props.sceneData.nodes) return true;
        
        return false;
    }

    handleClearMultiSelection = () => {
        if (this.props.setProperty) {
            this.props.setProperty('scene.multiSelectedNodes', []);
        }
    };

    handleClearSelection = () => {
        if (!this.props.setProperty) {
            return;
        }

        this.props.setProperty('scene.selectedNode.path', '');
        this.props.setProperty('scene.selectedNode', {
            name: '',
            path: '',
            worldPosition: [0, 0, 0],
            worldRotation: [0, 0, 0],
            worldScale: [1, 1, 1],
            position: [0, 0, 0],
            rotation: [0, 0, 0],
            scale: [1, 1, 1],
        });
        this.props.setProperty('scene.multiSelectedNodes', []);
    };

    handleToggleCoordinates = () => {
        this.setState((state) => {
            const coordinatesExpanded = !state.coordinatesExpanded;
            try {
                window.sessionStorage?.setItem(
                    coordinatesExpandedStorageKey,
                    String(coordinatesExpanded)
                );
            } catch {
                // Session storage can be unavailable in restricted browser contexts.
            }
            return { coordinatesExpanded };
        });
    };

    render() {
        const scene = this.props.sceneData;
        const hasHierarchy = scene.nodes !== '[]';
        const multiSelectedNodes = scene.multiSelectedNodes || [];
        const selectedNode = scene.selectedNode;
        const nodeSelected = selectedNode.path;
        const { coordinatesExpanded } = this.state;
        
        // Show multi-selection panel if multiple nodes selected
        if (multiSelectedNodes.length > 0) {
            return (
                <div className='selected-node-panel-parent'>
                    <MultiSelectionPanel 
                        nodes={multiSelectedNodes} 
                        onClear={this.handleClearMultiSelection}
                    />
                </div>
            );
        }
        
        // Show single selection panel
        if (!hasHierarchy || !nodeSelected) {
            return null;
        }

        return (
            <div className='selected-node-panel-parent'>
                <div className='selected-node-panel'>
                    <div className='selected-node-header'>
                        <span className='selected-node-title'>Selected Node</span>
                        <button
                            className='selected-node-close'
                            onClick={this.handleClearSelection}
                            title='Deselect'
                        >
                            ✕
                        </button>
                    </div>
                    <Row label='Name' value={selectedNode.name} copyText={selectedNode.name} showCopy={true} />
                    {selectedNode.meshName && <Row label='Mesh' value={selectedNode.meshName} copyText={selectedNode.meshName} showCopy={true} />}
                    {selectedNode.materialName && <Row label='Material' value={selectedNode.materialName} showCopy={false} />}
                    
                    {selectedNode.animations && selectedNode.animations.length > 0 && (
                        <div className='selected-node-section'>
                            <span className='selected-node-section-label'>Animations</span>
                            {selectedNode.animations.map((anim, index) => (
                                <div key={index} className='selected-node-row'>
                                    <span className='selected-node-value' style={{ flex: 1 }}>
                                        {anim.isActive ? '🔄 ' : ''}{anim.animationName}{anim.duration ? ` (${anim.duration.toFixed(2)}s)` : ''}
                                    </span>
                                    <button
                                        className='selected-node-copy'
                                        onClick={() => copyToClipboard(anim.animationName)}
                                        title='Copy animation name'
                                    >
                                        📋
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    <div className='selected-node-section selected-node-coordinates-section'>
                        <button
                            className='selected-node-section-toggle'
                            onClick={this.handleToggleCoordinates}
                            aria-expanded={coordinatesExpanded}
                            title={coordinatesExpanded ? 'Collapse coordinates' : 'Expand coordinates'}
                        >
                            <span className='selected-node-section-label'>Coordinates</span>
                            <span className='selected-node-section-toggle-icon'>
                                {coordinatesExpanded ? '-' : '+'}
                            </span>
                        </button>
                        {coordinatesExpanded && (
                            <div className='selected-node-coordinate-list'>
                                <Vector label='World Pos' dimensions={3} value={selectedNode.worldPosition} enabled={false}/>
                                <Vector label='World Rot' dimensions={3} value={selectedNode.worldRotation} enabled={false}/>
                                <Vector label='World Scale' dimensions={3} value={selectedNode.worldScale} enabled={false}/>
                                <Vector label='Position' dimensions={3} value={selectedNode.position} enabled={false}/>
                                <Vector label='Rotation' dimensions={3} value={selectedNode.rotation} enabled={false}/>
                                <Vector label='Scale' dimensions={3} value={selectedNode.scale} enabled={false}/>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default SelectedNode;
